import { useState, useEffect } from 'react';
import * as api from '../api/api';
import { normalizeItemFields } from '../utils/insights/itemNormalizers';
import { buildPaginationParams, buildFilterParams } from '../utils/insights/paramBuilders';
import { useTranslation } from 'react-i18next';

/**
 * Hook para cargar datos de la tabla de resúmenes
 * @param {Object} dateRange - Rango de fechas
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Array} selectedAgents - Agentes seleccionados
 * @param {Object} pagination - Objeto de paginación { currentPageIndex, pageKeys, setHasMorePages, updatePaginationKeys }
 * @param {Object} preferences - Preferencias de tabla { pageSize }
 * @returns {Object} { tableItems, loading, error, refreshData }
 */
export function useSummaryData(dateRange, selectedSupervisor, selectedAgents, pagination, preferences) {
  const { t } = useTranslation();
  const [tableItems, setTableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    currentPageIndex,
    pageKeys,
    setHasMorePages,
    updatePaginationKeys
  } = pagination;

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
        if (isMounted) {
          setTableItems([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Construir parámetros de paginación
        const paginationParams = buildPaginationParams(
          currentPageIndex,
          pageKeys,
          preferences.pageSize
        );

        // Construir parámetros de filtros
        const filterParams = buildFilterParams(
          selectedSupervisor,
          selectedAgents,
          dateRange
        );

        // Combinar parámetros
        const params = {
          ...filterParams,
          ...paginationParams
        };
        
        console.log("Buscando datos con parámetros:", params);
        const response = await api.getSummary(params);
        console.log("Respuesta completa de getSummary:", response);
        console.log("startKey en respuesta:", response.startKey);
        console.log("timestampFrom en respuesta:", response.timestampFrom, "tipo:", typeof response.timestampFrom);
        
        if (!isMounted) return;

        if (response && response.items && Array.isArray(response.items)) {
          console.log(`Se encontraron ${response.items.length} elementos`);
          // Normalizar los datos para manejar campos con mayúsculas
          const normalizedItems = response.items.map(item => normalizeItemFields(item, t));
          setTableItems(normalizedItems);
          
          // Guardar claves de paginación
          // Validar que timestampFrom sea un valor válido (no "0" ni vacío)
          let isValidTimestamp = response.timestampFrom && 
                                 response.timestampFrom !== "0" && 
                                 response.timestampFrom !== 0 &&
                                 parseInt(response.timestampFrom) > 0;
          
          let timestampToUse = response.timestampFrom;
          
          // WORKAROUND: Si el backend devuelve "0", extraer el timestamp del último item
          if (!isValidTimestamp && response.startKey && normalizedItems.length > 0) {
            const lastItem = normalizedItems[normalizedItems.length - 1];
            const lastTimestamp = lastItem.timestamp;
            
            if (lastTimestamp && lastTimestamp > 0) {
              // Intentar convertir timestamp ISO a número si es necesario
              const timestampNum = typeof lastTimestamp === 'string' 
                ? new Date(lastTimestamp).getTime() 
                : lastTimestamp;
              
              if (timestampNum > 0) {
                timestampToUse = String(timestampNum);
                isValidTimestamp = true;
                console.warn('⚠️ Backend devolvió timestampFrom=0. Usando timestamp del último item:', timestampToUse);
              }
            }
          }
          
          if (response.startKey && isValidTimestamp) {
            console.log(`Guardando claves de paginación en pageKeys[${currentPageIndex - 1}]:`, {
              startKey: response.startKey.substring(0, 50) + '...',
              timestampFrom: timestampToUse
            });
            updatePaginationKeys(currentPageIndex, response.startKey, timestampToUse);
          }
          
          // Usar hasMore del backend (no asumir true si hay startKey)
          const backendHasMore = response.hasMore === true;
          setHasMorePages(backendHasMore);
          console.log(`hasMore del backend: ${backendHasMore}`);
        } else {
          console.log("No se encontraron elementos");
          setTableItems([]);
          setHasMorePages(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setTableItems([]);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedSupervisor, selectedAgents, currentPageIndex, preferences.pageSize]);
  // Nota: pageKeys NO está en las dependencias porque se actualiza dentro del useEffect
  // y causaría un loop infinito. Solo se usa para construir parámetros, no para determinar cuándo hacer la llamada.

  const refreshData = () => {
    // Forzar re-render del useEffect
    setTableItems([...tableItems]);
  };

  return {
    tableItems,
    loading,
    error,
    refreshData
  };
}

